import * as z from "zod";
import { Brin6Schema, GradesSchema, InternalRoleSchema, UuidSchema } from "@spark/domain/schemas";

//#region src/session/legal/consent.constants.ts
/**
* @public
*/
const PERMISSIONS = [
	"functional",
	"limited-analytics",
	"analytics",
	"marketing"
];

//#endregion
//#region src/session/legal/terms.constants.ts
/**
* @public
*/
const TERM_VERSIONS = { "cookie-policy": [{ version: "2025-09" }] };
/**
* @public
*/
const TERMS = Object.keys(TERM_VERSIONS);
/**
* @public
*/
const TERM_STATES = [
	"viewed",
	"accepted",
	"rejected",
	"withdrawn"
];

//#endregion
//#region src/session/legal/consent.schemas.ts
const PermissionSchema = z.enum(PERMISSIONS).meta({
	id: "spark-permission",
	title: "Permission",
	description: "A permission that can be granted by the user for cookie categories",
	examples: PERMISSIONS
});
const ConsentSchema = z.object({
	functional: z.literal(true).default(true).meta({ description: "User consent for functional cookies" }),
	"limited-analytics": z.literal(true).default(true).meta({ description: "User consent for limited analytics cookies" }),
	analytics: z.boolean().default(false).meta({ description: "User consent for analytics cookies" }),
	marketing: z.boolean().default(false).meta({ description: "User consent for marketing cookies" })
}).meta({
	id: "consent",
	title: "User Consent",
	description: "User consent for various categories of cookies",
	examples: [{
		functional: true,
		"limited-analytics": true,
		analytics: false,
		marketing: false
	}]
});

//#endregion
//#region src/session/legal/terms.schemas.ts
const TermSchema = z.enum(TERMS).meta({
	id: "spark-term",
	title: "Term or policy",
	description: "A term or policy that users must accept to use the application",
	examples: TERMS
});
const TermVersionSchema = z.string().regex(/^\d{4}-\d{2}$/).brand("term-version").meta({
	id: "spark-term-version",
	title: "Term version",
	description: "Version of a term or policy in the format YYYY-MM",
	examples: ["2025-09"]
});
/**
* Term states that a user can have for a given policy.
*/
const TermStateSchema = z.enum(TERM_STATES).meta({
	id: "spark-term-state",
	title: "Term state",
	description: "State of a term or policy for a user",
	examples: TERM_STATES
});
/**
* Terms and policies accepted by the user with their accepted version.
*/
const AcceptedTermsSchema = z.partialRecord(TermSchema, TermVersionSchema).superRefine((val, ctx) => {
	for (const term in val) {
		const version = val[term];
		if (version === void 0) continue;
		const validVersions = TERM_VERSIONS[term] ?? [];
		if (!validVersions.some((v) => v.version === version)) ctx.addIssue({
			code: "custom",
			message: `Invalid version for term ${term}. Expected one of: ${validVersions.map((v) => v.version).join(", ")}`
		});
	}
}).meta({
	id: "spark-accepted-terms",
	title: "Accepted terms",
	description: "Terms and policies accepted by the user with their latest accepted version",
	examples: [{ "cookie-policy": "2025-09" }]
});

//#endregion
//#region src/session/claims.schemas.ts
/**
* User legal information stored in the session claims.
*/
const ClaimsLegalSchema = z.object({
	consent: ConsentSchema,
	terms: AcceptedTermsSchema
}).meta({ description: "User legal information" });
/**
* Teacher-specific claims.
*/
const ClaimsTeacherSchema = z.object({ id: UuidSchema.meta({ description: "Teacher ID" }) }).meta({ description: "Claims specific to teachers" });
const ClaimsStudentSchema = z.object({
	id: UuidSchema.meta({ description: "Student ID" }),
	group: z.object({
		id: UuidSchema.meta({ description: "Group ID" }),
		name: z.string().meta({ description: "Group name" }),
		grades: GradesSchema
	})
}).meta({ description: "Claims specific to students" });
const ClaimsSchoolSchema = z.object({
	id: UuidSchema.meta({ description: "School ID" }),
	name: z.string().meta({ description: "School name" }),
	brin: Brin6Schema.meta({ description: "School BRIN code" }).optional()
}).meta({ description: "Teacher or student school information" });
/**
* JWT standard claims.
*/
const JwtClaimsSchema = z.object({
	iss: z.string().meta({ description: "Issuer of the token" }),
	sub: UuidSchema.meta({ description: "User ID" }),
	sid: UuidSchema.meta({ description: "Session ID" }),
	exp: z.number().meta({ description: "Expiration timestamp" }),
	iat: z.number().meta({ description: "Issued at timestamp" })
}).meta({ description: "JWT standard claims" });
/**
* Base claims for both teachers and students, extending the standard JWT claims with additional user information.
*/
const NameClaimsSchema = z.object({
	first: z.string().meta({ description: "User's first name" }),
	particle: z.string().nullable().meta({ description: "User's name particle" }),
	last: z.string().meta({ description: "User's last name" })
});
/**
* Base claims for both teachers and students.
*
* Extends the standard JWT claims with additional user information.
*/
const BaseClaimsSchema = JwtClaimsSchema.extend({
	name: z.string().meta({
		description: "User name",
		deprecated: true
	}),
	nextName: NameClaimsSchema.meta({ description: "User name details" }),
	roles: z.array(InternalRoleSchema),
	legal: ClaimsLegalSchema
});
/**
* Claims stored in the session token.
*/
const ClaimsSchema = z.preprocess(convertLegacy, z.union([
	BaseClaimsSchema.extend({
		school: ClaimsSchoolSchema,
		teacher: ClaimsTeacherSchema
	}).meta({ description: "Claims for teachers" }),
	BaseClaimsSchema.extend({
		school: ClaimsSchoolSchema,
		student: ClaimsStudentSchema
	}).meta({ description: "Claims for students" }),
	BaseClaimsSchema
])).meta({
	id: "session-claims",
	title: "Session Claims",
	description: "Session claims",
	examples: [
		{
			iss: "test-issuer-fixtures",
			sub: "b9f0da1a-bf82-4650-bebe-b7554166459a",
			sid: "2aa03e8d-c61e-4873-9c8a-c75575e0a09a",
			exp: 1779089239232,
			iat: 1749284698440,
			name: "Taylor Rutherford",
			nextName: {
				first: "Taylor",
				particle: null,
				last: "Rutherford"
			},
			roles: ["Spark.beheer/*/edit"],
			legal: {
				consent: {
					functional: true,
					"limited-analytics": true,
					analytics: false,
					marketing: false
				},
				terms: {}
			},
			school: {
				id: "9d4f3c2a-1b5e-4a7f-8c3d-6e2f1a4b9c7d",
				name: "Test School",
				brin: "20NE84"
			},
			teacher: { id: "045fca53-fe3c-4c04-a226-ccde067576dc" }
		},
		{
			iss: "test-issuer-fixtures",
			sub: "a91f14f4-36da-487a-9353-ca6de4cbdffb",
			sid: "2d7c0684-4f37-4138-822a-3be86d72c75b",
			exp: 1775917213159,
			iat: 1759442990170,
			name: "Leticia Runte",
			nextName: {
				first: "Leticia",
				particle: null,
				last: "Runte"
			},
			roles: ["Spark.method/spelling-grammatica/*", "Spark.beheer/*/edit"],
			legal: {
				consent: {
					functional: true,
					"limited-analytics": true,
					analytics: false,
					marketing: false
				},
				terms: {}
			},
			school: {
				id: "5c8e2b9f-4a1d-4e3b-9f2c-7a5b3d1c4e2f",
				name: "Student School",
				brin: "47RH44"
			},
			student: {
				id: "33781d12-eb31-4c0d-8498-7c5612d402e7",
				group: {
					id: "7ca5c047-842f-4302-a514-b9240b37bf11",
					name: "1A",
					grades: [
						"PO_1",
						"PO_2",
						"PO_4",
						"PO_5",
						"PO_7",
						"PO_8"
					]
				}
			}
		},
		{
			iss: "test-issuer-fixtures",
			sub: "a04dfa08-fccc-43c9-8714-195078ab95c2",
			sid: "254d92fd-e706-4f53-878e-1ac332d392fd",
			exp: 1794842819355,
			iat: 1748945641687,
			name: "Virgil Hodkiewicz",
			nextName: {
				first: "Virgil",
				particle: null,
				last: "Hodkiewicz"
			},
			roles: ["Spark.method/bwgeintegreerd/edit", "Spark.method/*/*"],
			legal: {
				consent: {
					functional: true,
					"limited-analytics": true,
					analytics: false,
					marketing: false
				},
				terms: {}
			}
		}
	]
});
/**
* @param claims Possible [legacy] claims
* @returns Original input or legacy teacher claims converted to teacher claims.
*/
function convertLegacy(claims) {
	if (typeof claims === "object" && claims !== null) {
		const { school, teacher, student, ...rest } = claims;
		if (school && !teacher && !student) return {
			...rest,
			school,
			teacher: { id: rest.sub }
		};
	}
	return claims;
}

//#endregion
export { TermVersionSchema as a, TERMS as c, PERMISSIONS as d, TermStateSchema as i, TERM_STATES as l, AcceptedTermsSchema as n, ConsentSchema as o, TermSchema as r, PermissionSchema as s, ClaimsSchema as t, TERM_VERSIONS as u };